import React from 'react'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table"
import { Spinner } from '@/components/ui/shadcn-io/spinner';
import type { PropertyListings } from '@/types/PropertyListings';
import { Badge } from '@/components/ui/badge';
import Helper from '@/lib/helper';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Edit, RotateCcw, Trash, Sparkles } from 'lucide-react';
import { differenceInDays, format } from 'date-fns';

interface _ExpiredListingTable {
    loading: boolean;
    listings: PropertyListings[] | null;
    startIndex?: number;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setSelectedListingId: React.Dispatch<React.SetStateAction<number | null>>;
    setActionType: React.Dispatch<React.SetStateAction<'delete' | 'deactivate' | 'close' | 'reopen' | 'activate' | 'repost'>>;
}

const ExpiredListingTable : React.FC<_ExpiredListingTable> = ({
    loading,
    listings,
    startIndex = 0,
    setOpen,
    setSelectedListingId,
    setActionType
}) => {
    const getExpiryDate = (listing: PropertyListings) => {
        if (listing.expiryDate) return new Date(listing.expiryDate);
        const createdAt = listing.createdAt ? new Date(listing.createdAt) : new Date();
        return new Date(createdAt.getFullYear(), createdAt.getMonth() + 1, createdAt.getDate(), createdAt.getHours(), createdAt.getMinutes(), createdAt.getSeconds());
    };

    const getExpiredAgo = (listing: PropertyListings) => {
        const days = differenceInDays(new Date(), getExpiryDate(listing));
        if (days <= 0) return 'Expired today';
        return `Expired ${days} day${days > 1 ? 's' : ''} ago`;
    };

    const triggerAction = (id: number, type: 'delete' | 'repost') => {
        setSelectedListingId(id);
        setActionType(type);
        setOpen(true);
    };

    return (
        <Table className='mt-5'>
            <TableHeader>
                <TableRow>
                    <TableHead className='w-[500px]'>Listing</TableHead>
                    <TableHead>Expired</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
            {
                loading ? (
                    <TableRow>
                        <TableCell colSpan={4}>
                            <div className='flex justify-center py-4'>
                                <Spinner />
                            </div>
                        </TableCell>
                    </TableRow>
                ) 
                : 
                listings && listings.length > 0 ? (
                    listings.map((listing, index) => (
                        <TableRow key={listing.id}>
                            <TableCell className='w-[500px]'>
                                <div className='flex items-center'>
                                    <span className='w-6 mr-2 flex-shrink-0 self-center text-right text-xs font-medium text-gray-400 tabular-nums'>
                                        {startIndex + index + 1}
                                    </span>
                                    <div 
                                    className='rounded-lg h-[100px] w-[100px] flex-shrink-0'
                                    style={{ 
                                        height: 100,
                                        width: 100,
                                        background: `url(${
                                        import.meta.env.VITE_AWS_S3}/${(listing.medias.find(x => x.mediaType === 1 && x.isCover === 1)?.path ||
                                            listing.medias.find(x => x.mediaType === 1)?.path)})`,
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center center'
                                    }}/>
                                    <div className='px-3'>
                                        <div className='font-medium'>{listing.headline}</div>
                                        <div className='text-xs mt-1 text-muted-foreground whitespace-break-spaces'>{listing.rawAddress}</div>
                                        <div className='font-bold mt-2'>{Helper.formatCurrency(Number(listing.price))}</div>
                                        <div className='flex items-center gap-2 mt-1.5'>
                                            <Badge variant={listing.forSaleOrRentId === 1 ? 'outline' : 'secondary'}>{listing.forSaleOrRentId === 1 ? 'Sale' : 'Rent'}</Badge>
                                            {listing.createChannel === 'ai' && (
                                                <Badge variant='outline' className='gap-1 border-violet-200 bg-violet-50 text-violet-700' title='Created with AI'>
                                                    <Sparkles className='h-3 w-3' /> AI
                                                </Badge>
                                            )}
                                            <span className='text-[11px] text-gray-700 font-mono bg-gray-100 px-1.5 py-0.5 rounded'>
                                                {listing.listingReferenceNumber}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </TableCell>
                            <TableCell>
                                <div className='text-sm font-medium'>{getExpiredAgo(listing)}</div>
                                <div className='text-xs text-muted-foreground mt-1'>
                                    {format(getExpiryDate(listing), 'dd MMM yyyy')}
                                </div>
                            </TableCell>
                            <TableCell>
                                <Badge className='bg-red-50 text-red-700 border border-red-200 hover:bg-red-50'>Expired</Badge>
                            </TableCell>
                            <TableCell>
                                <div className='flex flex-nowrap items-center gap-2'>
                                    <Button 
                                    type='button' 
                                    size={'sm'} 
                                    variant={'outline'}
                                    onClick={() => triggerAction(listing.id, 'repost')}>
                                        <RotateCcw className='h-3.5 w-3.5'/>
                                        Repost
                                    </Button>
                                    <Button 
                                    type='button' 
                                    size={'sm'} 
                                    variant={'outline'}
                                    asChild>
                                        <Link to={`/listings/edit/${listing.id}`}>
                                            <Edit className='h-3.5 w-3.5'/>
                                            Edit
                                        </Link>
                                    </Button>
                                    <Button 
                                    type='button' 
                                    size={'sm'} 
                                    variant={'destructive'}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        triggerAction(listing.id, 'delete');
                                    }}>
                                        <Trash className='h-3.5 w-3.5'/>
                                        Delete
                                    </Button>
                                </div>
                            </TableCell>
                        </TableRow>
                    ))
                ) : 
                (
                    <TableRow>
                        <TableCell colSpan={4} className="text-center py-4">
                            No listings found.
                        </TableCell>
                    </TableRow>
                )
            }
            </TableBody>
        </Table>
    )
}

export default ExpiredListingTable
